import React, { useState } from 'react';
import { Progress } from '@backstage/core-components';
import TuneIcon from '@material-ui/icons/Tune';
import { useLocation, useNavigate } from 'react-router-dom';
import SelectableFilter from './components/SidebarFilters';
import { ApiExplorerMessage } from './components/ApiExplorerMessage';
import { useApiFacets, useProductsPage } from './hooks';
import { API_EXPLORER_PATH, ApiExplorerLocationState } from './types';
import { useApiExplorerSearchParams } from './hooks/useApiExplorerSearchParams';
import LoadMoreCard from './components/LoadMoreCard';
import ApiCard from './components/ApiCard';
import {
  Container,
  Toolbar,
  ToolbarSpacer,
  ProductsCount,
  ContentRow,
  MainContent,
  CardsWrapper,
  ShowFiltersButton,
  FilterCountBadge,
} from './CustomApiExplorer.styles';

const CustomApiExplorer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tags, search, setTags, setSearch, clearAll } =
    useApiExplorerSearchParams();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const openProduct = (productId: string) => {
    const state: ApiExplorerLocationState = { fromSearch: location.search };
    navigate(`${API_EXPLORER_PATH}/${productId}`, { state });
  };

  const {
    products,
    total,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isPlaceholderData,
    showLoadMore,
    fetchNextPage,
    isError,
    hasDocs,
  } = useProductsPage({ search, tags });

  const { options: apiTypesOptions } = useApiFacets();

  if (isLoading && products.length === 0) {
    return <Progress />;
  }

  const hasActiveFilters = Boolean(search) || tags.length > 0;
  const activeFiltersCount = tags.length + (search ? 1 : 0);

  return (
    <Container className={isFiltersOpen ? 'filters-open' : undefined}>
      <Toolbar>
        {!isFiltersOpen ? (
          <ShowFiltersButton
            className={activeFiltersCount > 0 ? 'has-filters' : undefined}
            startIcon={<TuneIcon />}
            onClick={() => setIsFiltersOpen(true)}
          >
            Show Filters
            {activeFiltersCount > 0 && (
              <FilterCountBadge>{activeFiltersCount}</FilterCountBadge>
            )}
          </ShowFiltersButton>
        ) : (
          <ToolbarSpacer />
        )}
        {products.length > 3 && (
          <ProductsCount variant="body1">
            Showing <b>{products.length}</b> out of{' '}
            <b>{total - (hasDocs ? 1 : 0)}</b>
            {/* TODO: remove -1 with hasDocs when BE adds filter to remove docs.  */}
          </ProductsCount>
        )}
      </Toolbar>

      <ContentRow>
        {isFiltersOpen && (
          <SelectableFilter
            options={apiTypesOptions}
            selectedOptions={tags}
            searchQuery={search}
            onSelectionChange={setTags}
            onSearchChange={setSearch}
            onClearAll={clearAll}
            onClose={() => setIsFiltersOpen(false)}
          />
        )}

        <MainContent>
          {isError && (
            <ApiExplorerMessage
              title="Failed to load APIs"
              description="Something went wrong while loading APIs. Please try again."
            />
          )}
          {!isError && products.length === 0 && !isFetching && (
            <ApiExplorerMessage
              title="No Results Found"
              description={
                hasActiveFilters
                  ? "We couldn't find any products matching your current filters. Try adjusting your search or clearing filters."
                  : 'No APIs are registered in the catalog yet.'
              }
              onClearFilters={hasActiveFilters ? clearAll : undefined}
            />
          )}
          {!isError && (products.length > 0 || isFetching) && (
            <CardsWrapper style={{ opacity: isPlaceholderData ? 0.6 : 1 }}>
              {products.map(product => (
                <ApiCard
                  key={product.id}
                  {...product}
                  productType={product.tag}
                  subtitle={product.specs[0]?.version}
                  image={product.thumbnails[0]?.url}
                  isNew={product.new}
                  isDeprecated={product.deprecated}
                  onActionClick={() => openProduct(product.id)}
                />
              ))}
              {showLoadMore && !isPlaceholderData && (
                <LoadMoreCard
                  isLoading={isFetchingNextPage}
                  onClick={() => {
                    fetchNextPage();
                  }}
                />
              )}
            </CardsWrapper>
          )}
        </MainContent>
      </ContentRow>
    </Container>
  );
};

export default CustomApiExplorer;
